// Export Workflow — generic per-phase ASSESSMENT QUALITY helpers (policy-refusal resilience
// plan §5.6 / Slice 6). ONE place owns how `data.phase_assessments` is read, how older saved
// runs (saved before the record existed) are inferred, and the provider-neutral wording — so
// the live cards/timeline (EARChatUI), reload, Saved Work (ConsolidatedResults, 'Export Unified'
// branch) and the downloadable PDF (EntityTicketPdf) all agree (Invariant 10). Pure TS (no React)
// so the PDF builder can use it.
//
// Rules:
//   * `phase_assessments` is authoritative whenever the key is PRESENT on `data` (every live
//     frame carries it, and the server already folds its own legacy inference into it). Only
//     when the key is ABSENT (a run saved before Slice 3) do we infer from the known historical
//     placeholder text — mirroring pyapi `_legacy_phase_assessment`. Inference only ever marks
//     a phase not_assessed on a positive placeholder match; it never manufactures "assessed".
//   * Every not_assessed record renders the same customer copy. The internal failure_reason
//     (provider_refusal, …) is never shown, and fallback provenance is not on the wire (D3).
import {
  ExportAssessmentPhase,
  PhaseAssessment,
  PhaseAssessments,
} from '../interfaces/iStreamedResponse';

// Assessment keys, in workflow order. The Country and Control List Check (phase "country") is
// ONE phase that reports TWO assessments: "country" (the destination screen) and
// "export_screening" (the commodity control-list screen — the pre-merge "Sensitive Tech
// Jurisdictions" key, kept so old and new runs read the same).
export const EXPORT_ASSESSMENT_PHASES: ExportAssessmentPhase[] = [
  'profile',
  'schedule_b',
  'country',
  'export_screening',
  'nrc',
  'itar',
  'ccl',
];

// The workflow's phases (one per Classification Progress card), in order.
export const EXPORT_REVIEW_PHASES: ExportAssessmentPhase[] = [
  'profile',
  'schedule_b',
  'country',
  'nrc',
  'itar',
  'ccl',
];

// Same wording as the live Classification Progress timeline headings.
export const REVIEW_PHASE_LABELS: Record<ExportAssessmentPhase, string> = {
  profile: 'Product Profile',
  schedule_b: 'Schedule B / HTS Classification',
  country: 'Country and Control List Check',
  export_screening: 'Country and Control List Check',
  nrc: 'Nuclear (NRC + DOE)',
  itar: 'ITAR Check',
  ccl: 'EAR / CCL Check',
};

// Per-ASSESSMENT wording (Manual Review lists): names which half of the merged phase failed.
export const ASSESSMENT_PHASE_LABELS: Record<ExportAssessmentPhase, string> = {
  ...REVIEW_PHASE_LABELS,
  country: 'Country and Control List Check (destination country)',
  export_screening: 'Country and Control List Check (control lists)',
};

/** The phase an assessment key belongs to ("export_screening" → "country"). */
export const phaseOfAssessment = (key: ExportAssessmentPhase): ExportAssessmentPhase =>
  key === 'export_screening' ? 'country' : key;

// Pill text + the plan §6 "all eligible fallbacks fail" copy. Provider-neutral by design.
export const NOT_ASSESSED_LABEL = 'Not assessed — manual review required';
export const NOT_ASSESSED_NOTICE =
  'This automated phase could not be completed for this transaction. No negative or ' +
  'low-risk conclusion has been inferred. The verified findings shown elsewhere remain ' +
  'available, but this phase requires review by a qualified export-compliance professional.';

// The export workflow's slim UI `data` object (still loosely typed app-wide — see
// iStreamedResponse `data?: any`); only the keys read below matter here.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ExportAssessmentData = Record<string, any> | null | undefined;

const FAILURE_REASONS = new Set([
  'provider_refusal',
  'transient_exhausted',
  'invalid_output',
  'provider_error',
  'ineligible_data',
  'upstream_not_assessed',
  'missing_input',
]);

/** Allow-list one record (mirrors pyapi assessment_status.normalize_record). */
export function normalizePhaseAssessment(raw: unknown): PhaseAssessment | null {
  if (!raw || typeof raw !== 'object') return null;
  const value = raw as Record<string, unknown>;
  if (value.assessment_status === 'not_assessed') {
    const reason = String(value.failure_reason || '');
    return {
      assessment_status: 'not_assessed',
      review_required: true,
      failure_reason: (FAILURE_REASONS.has(reason)
        ? reason
        : 'provider_error') as PhaseAssessment['failure_reason'],
    };
  }
  if (value.assessment_status === 'assessed') {
    return {
      assessment_status: 'assessed',
      review_required: Boolean(value.review_required),
      failure_reason: null,
    };
  }
  return null;
}

const lower = (v: unknown): string => String(v ?? '').trim().toLowerCase();

const isBlankOrPlaceholder = (v: unknown): boolean => {
  if (v === undefined || v === null) return true;
  const s = String(v).trim();
  return s === '' || ['.', 'N/A', 'NA', 'NONE', 'null'].includes(s);
};

/** A regime `controlled` string that is a refusal / incomplete-automation placeholder. */
export function isRegimeNotAssessedText(value: unknown): boolean {
  const s = lower(value);
  if (!s) return false;
  if (s.startsWith('unable to complete automated')) return true;
  return s.startsWith('not assessed') && s.includes('automated') && s.includes('incomplete');
}

const NOT_ASSESSED: PhaseAssessment = {
  assessment_status: 'not_assessed',
  review_required: true,
  failure_reason: 'invalid_output',
};

/**
 * Conservative inference for runs saved BEFORE `phase_assessments` existed. Returns a
 * not_assessed record only on a positive match against a known historical placeholder;
 * otherwise null (unknown — the surface renders exactly as it did before).
 */
export function inferLegacyPhaseAssessment(
  data: ExportAssessmentData,
  phase: ExportAssessmentPhase,
): PhaseAssessment | null {
  if (!data || typeof data !== 'object') return null;
  switch (phase) {
    case 'schedule_b': {
      const sb = data.schedule_b || {};
      const hasOtherOutput = Boolean(sb.section || sb.chapter || sb.product_name);
      return hasOtherOutput && isBlankOrPlaceholder(sb.schedule_b_code) && !sb.not_applicable
        ? NOT_ASSESSED
        : null;
    }
    case 'country': {
      const c = data.country_screening || {};
      return c.normalized_country_name &&
        (c.has_any_country_risk_hit === null || c.has_any_country_risk_hit === undefined)
        ? NOT_ASSESSED
        : null;
    }
    case 'export_screening': {
      const t = data.export_screening || {};
      if (t.not_applicable) return null;
      const summary = Array.isArray(t.summary) ? t.summary.join(' ') : t.summary;
      const text = `${lower(summary)} ${lower(t.explanation)}`;
      if (
        text.includes('automated commodity-risk assessment incomplete') ||
        text.includes('no determination was produced') ||
        text.includes('did not produce a usable result')
      ) {
        return NOT_ASSESSED;
      }
      return null;
    }
    case 'nrc': {
      const n = data.nrc || {};
      return isRegimeNotAssessedText(n.controlled) || isRegimeNotAssessedText(n.doe_controlled)
        ? NOT_ASSESSED
        : null;
    }
    case 'itar':
      return isRegimeNotAssessedText((data.itar || {}).controlled) ? NOT_ASSESSED : null;
    case 'ccl': {
      const headline = lower((data.ear_ccl || {}).classification_result);
      return headline.includes('could not be finalized automatically') ||
        headline.includes('could not be completed automatically') ||
        headline.includes('assessment incomplete')
        ? NOT_ASSESSED
        : null;
    }
    default:
      return null;
  }
}

/** The normalized record for one phase (authoritative map, else legacy inference). */
export function getPhaseAssessment(
  data: ExportAssessmentData,
  phase: ExportAssessmentPhase,
): PhaseAssessment | null {
  const map = data?.phase_assessments;
  if (map && typeof map === 'object') {
    return normalizePhaseAssessment((map as PhaseAssessments)[phase]);
  }
  return inferLegacyPhaseAssessment(data, phase);
}

// Upfront scoped review: a phase the user did not ask to run (pyapi `_review_scope_for_wire`,
// `data.review_scope.not_requested`). Distinct from not_assessed — nothing failed — so it never
// raises Manual Review, and never reads as cleared either.
export const NOT_REQUESTED_LABEL = 'Not requested (scoped review)';

/** True when the user scoped this phase out of the run (absent on full reviews / older runs).
 *  The control-list key answers for its merged phase. */
export function isPhaseNotRequested(data: ExportAssessmentData, phase: ExportAssessmentPhase): boolean {
  const list = data?.review_scope?.not_requested;
  return Array.isArray(list) && (list.includes(phase) || list.includes(phaseOfAssessment(phase)));
}

/** True when the phase's automated assessment is unusable and needs qualified review. */
export function isPhaseNotAssessed(data: ExportAssessmentData, phase: ExportAssessmentPhase): boolean {
  if (isPhaseNotRequested(data, phase)) return false;
  return getPhaseAssessment(data, phase)?.assessment_status === 'not_assessed';
}

/** True when a phase — or either part of the merged Country and Control List Check — needs
 *  qualified review (drives the phase's card status / badge). */
export function isPhaseOrPartNotAssessed(
  data: ExportAssessmentData,
  phase: ExportAssessmentPhase,
): boolean {
  if (phase === 'country') {
    return isPhaseNotAssessed(data, 'country') || isPhaseNotAssessed(data, 'export_screening');
  }
  return isPhaseNotAssessed(data, phase);
}

/**
 * Badge wording for a not-assessed phase. The Country and Control List Check names the PART that
 * wasn't assessed when the other part ran (e.g. "Continue without a code": the destination was
 * screened, the control lists weren't — Bug 16); everything else keeps the generic copy.
 */
export function notAssessedBadgeCopy(
  data: ExportAssessmentData,
  phase: ExportAssessmentPhase,
): { label: string; notice: string } {
  if (phase === 'country') {
    const countryNA = isPhaseNotAssessed(data, 'country');
    const listsNA = isPhaseNotAssessed(data, 'export_screening');
    if (listsNA && !countryNA) {
      return {
        label: 'Control lists not assessed — manual review required',
        notice: controlListsNotRunText(data),
      };
    }
    if (countryNA && !listsNA) {
      return {
        label: 'Destination screen not assessed — manual review required',
        notice:
          'The control lists were checked, but the destination-country screen could not be ' +
          'completed for this transaction. No negative or low-risk conclusion has been inferred ' +
          'for the destination; it requires review by a qualified export-compliance professional.',
      };
    }
  }
  return { label: NOT_ASSESSED_LABEL, notice: NOT_ASSESSED_NOTICE };
}

/** Why the control-list part of the Country and Control List Check has no result (its chat card
 *  and the panel badge). Empty when that part was assessed. */
export function controlListsNotRunText(data: ExportAssessmentData): string {
  const record = getPhaseAssessment(data, 'export_screening');
  if (record?.assessment_status !== 'not_assessed') return '';
  if (record.failure_reason === 'missing_input') {
    return (
      'The destination country was screened. The control lists were not checked because there ' +
      'is no Schedule B / HTS code (you continued without one), and the lists are matched on ' +
      'that code. No low-risk conclusion has been drawn for them; they need review by a ' +
      'qualified export-compliance professional.'
    );
  }
  return (
    'The control-list check could not be completed for this transaction. No negative or ' +
    'low-risk conclusion has been inferred; it requires review by a qualified ' +
    'export-compliance professional.'
  );
}

/** Phases whose automated assessment could not be completed, in workflow order. */
export function notAssessedPhases(data: ExportAssessmentData): ExportAssessmentPhase[] {
  return EXPORT_ASSESSMENT_PHASES.filter((p) => isPhaseNotAssessed(data, p));
}

export const NUCLEAR_INCOMPLETE_HEADLINE =
  'Nuclear screening incomplete — manual review recommended (NRC / DOE Part 810)';

/**
 * The COMBINED NRC + DOE headline shared by the live card (NRCResults) and the PDF
 * — one implementation so the two can never drift (the pre-Slice-6 copies string-matched the
 * old "unable to complete" placeholder and collapsed a refused NRC to "No clear nuclear match").
 * A DOE-only match is never hidden behind a Part 110 "no match"; a not-assessed leg reads as
 * incomplete unless either leg carries a verified positive hit (positive findings survive).
 * Returns null when neither the NRC nor the DOE leg has a value yet and the phase is not
 * recorded as not_assessed.
 */
export function combinedNuclearHeadline(data: ExportAssessmentData): string | null {
  const nrc = data?.nrc || {};
  const controlled = nrc.controlled;
  const doe = nrc.doe_controlled;
  const hasControl =
    controlled !== undefined && controlled !== null && String(controlled).trim() !== '';
  const hasDoe = doe !== undefined && doe !== null && String(doe).trim() !== '';
  const bucket = (v: unknown): string => {
    const s = lower(v);
    if (s.includes('potential') && s.includes('match identified')) return 'hit';
    if (s.includes('insufficient')) return 'insufficient';
    if (isRegimeNotAssessedText(v)) return 'refused';
    return 'no_match';
  };
  const bn = bucket(controlled);
  const bd = hasDoe ? bucket(doe) : 'none';
  if (bn === 'hit' || bd === 'hit') {
    return hasDoe
      ? 'Potential nuclear match identified (NRC Part 110 / DOE Part 810)'
      : String(controlled);
  }
  // The authoritative not_assessed record wins even when neither leg carries a value (a failed
  // phase with no placeholder text must read "incomplete", never "Awaiting information" / N/A).
  if (isPhaseNotAssessed(data, 'nrc') || bn === 'refused' || bd === 'refused') {
    return NUCLEAR_INCOMPLETE_HEADLINE;
  }
  // Only bail when NEITHER leg has a value — a DOE-only match must still surface.
  if (!hasControl && !hasDoe) return null;
  if (!hasDoe) return String(controlled);
  if (bn === 'insufficient' || bd === 'insufficient') {
    return 'Nuclear screening — limited information (NRC Part 110 / DOE Part 810)';
  }
  return 'No clear nuclear match identified (NRC Part 110 / DOE Part 810)';
}

/** One provider-neutral sentence naming the unassessed checks (shared by final card,
 *  and the PDF so both read identically). Empty when none. */
export function notAssessedSummaryText(phases: ExportAssessmentPhase[]): string {
  if (!phases.length) return '';
  const names = phases.map((p) => ASSESSMENT_PHASE_LABELS[p]).join(', ');
  return (
    `The automated assessment could not be completed for: ${names}. No negative or ` +
    'low-risk conclusion has been inferred for these checks, and no overall clearance ' +
    'should be relied on until they are reviewed by a qualified export-compliance professional.'
  );
}

/** Timeline/presentation state: an operationally settled phase whose assessment is not
 *  usable becomes `needs_review` (UI-only — backend `phase_status` stays `complete`). */
export function presentationStatusFor<S extends string>(
  status: S,
  data: ExportAssessmentData,
  phase: ExportAssessmentPhase,
): S | 'needs_review' {
  return status === 'complete' && isPhaseNotAssessed(data, phase) ? 'needs_review' : status;
}

/**
 * Why a phase was NOT PERFORMED because an exclusive Nuclear / ITAR result stopped the run
 * (jurisdiction routing locks the later checks as settled so they can't re-run). '' otherwise.
 * Reads the fixed placeholders the backend writes for a locked phase, so saved runs work too.
 */
export function phaseNotPerformedReason(data: ExportAssessmentData, phase: string): string {
  const reasonOf = (value: unknown) => (typeof value === 'string' ? value.trim() : '');
  if (phase === 'ccl') {
    const result = reasonOf(data?.ear_ccl?.classification_result);
    return /^EAR \/ CCL assessment not (performed|completed)\b/i.test(result)
      ? reasonOf(data?.ear_ccl?.reasoning) || result
      : '';
  }
  if (phase === 'itar') {
    return /^Skipped \(nuclear-controlled\)/i.test(reasonOf(data?.itar?.controlled))
      ? reasonOf(data?.itar?.reason) || 'Not performed: a nuclear result concluded the jurisdiction.'
      : '';
  }
  if (phase === 'nrc') {
    return /^Not assessed — ITAR-controlled/i.test(reasonOf(data?.nrc?.controlled))
      ? reasonOf(data?.nrc?.reason) || 'Not performed: an ITAR / USML result concluded the jurisdiction.'
      : '';
  }
  return '';
}
